import React from "react";
import MapFull from "../common/components/MapFull";
import BackButton from "../common/components/BackButton";
import FloatingTopBar from "../components/FloatingTopBar";
import UserPosition from "../common/components/UserPosition";
import TimeLine from "../components/TimeLine";
import { Fab } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Annotation } from "../common/classes/annotation";
import Annotate from "../common/dialogs/Annotate";
import AnnotationMarker from "../common/components/AnnotationMarker";
import { MapCameraChangedEvent, MapCameraProps } from "@vis.gl/react-google-maps";
import { useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import { computeZoom } from "../common/utils/position";
import Loading from "../common/components/Loading";
import { and, whereNotDeleted } from "../common/utils/graphql";
import { IMAGE_CVI_TIFF, IMAGE_GNDVI_TIFF, IMAGE_GRVI_TIFF, IMAGE_KEY, IMAGE_LCI_TIFF, IMAGE_NDREI_TIFF, IMAGE_NDVI_TIFF, IMAGE_NDWI_TIFF, IMAGE_ORTHOPHOTO, IMAGE_OSAVI_TIFF, IMAGE_VARI_TIFF } from "../common/hooks/useCache";
import AnalysisLayer from "../common/components/AnalysisLayer";
import { db } from "../common/db/db";
import IndexSlider from "../common/components/IndexSlider";
import { getIndex, globalAvailableIndexes } from "../utils/indexes";
import Polyline from "../common/components/Polyline";
import { useScreenSize } from "../common/hooks/useScreenSize";
import { getMarksForPhase } from "../utils/phases";

function Compare() {
  const [ latLngs, setLatLngs ] = React.useState<google.maps.LatLngLiteral[]>([]);
  const [ selectedRange, setSelectedRange ] = React.useState<[number, number] | undefined>(undefined);
  const [ min, setMin ] = React.useState<number | undefined>(-1);
  const [ max, setMax ] = React.useState<number | undefined>(1);
  const [ compareSelectedRange, setCompareSelectedRange ] = React.useState<[number, number] | undefined>(undefined);
  const [ compareMin, setCompareMin ] = React.useState<number | undefined>(-1);
  const [ compareMax, setCompareMax ] = React.useState<number | undefined>(1);
  const [ cameraState, setCameraState ] = React.useState<MapCameraProps | undefined>();
  const [ annotateDialogOpen, setAnnotateDialogOpen ] = React.useState<boolean>(false);
  const [ clickLatLng, setClickLatLng ] = React.useState<google.maps.LatLngLiteral | null>(null);
  const [ showButtons, setShowButtons ] = React.useState<boolean>(true);
  const [ locked, setLocked ] = React.useState<boolean>(true);
  const [ annotations, setAnnotations ] = React.useState<Annotation[]>([]);
  const [ availableIndexes, setAvailableIndexes ] = React.useState<string[]>([]);
  const [ selectedIndex, setSelectedIndex ] = React.useState<string | undefined>();
  const [ marks, setMarks ] = React.useState<{ value: number, label: string }[] | undefined>(undefined);
  const [ compareMarks, setCompareMarks ] = React.useState<{ value: number, label: string }[] | undefined>(undefined);
  const [ compareAnalysis, setCompareAnalysis ] = React.useState<{
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      phase: string,
    }[],
  } | null>(null);
  const [ currentAnalysis, setCurrentAnalysis ] = React.useState<{
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      phase: string,
    }[],
  } | null>(null);
  const [ analysis, setAnalysis ] = React.useState<{
    id: number,
    uuid: string,
    created_at: string,
    agriculture_analysis_data: {
      id: number,
      orthophoto_png: string,
      phase: string,
      index_data: {
        name: string,
        png: string,
        tiff: string,
        average: number,
        min: number,
        max: number,
        std_dev: number,
      }[],
    }[],
  }[]>([]);
  const [ field, setField ] = React.useState<{
    id: number,
    uuid: string,
    name: string,
  } | null>(null);
  const [ defaultCenter, setDefaultCenter ] = React.useState<google.maps.LatLngLiteral | undefined>();
  const [ defaultZoom, setDefaultZoom ] = React.useState<number | undefined>();

  const url = new URL(window.location.href);
  const analysisUUID = url.searchParams.get('analysis');
  const indexName = url.searchParams.get('index');

  const { vertical } = useScreenSize();

  if (indexName && !selectedIndex) {
    setSelectedIndex(indexName);
  }

  const { fieldId, compareAnalysisID, compareIndex } = useParams();

  const { loading, error, data } = useQuery(gql(`
    query FieldDetails($fieldId: uuid!) {
      fields(where: {uuid: {_eq: $fieldId}}) {
        id
        uuid
        name
        analyses(where: `+and(whereNotDeleted() + '{type: {_eq: "agriculture"}}')+`, order_by: {created_at: desc}) {
          uuid
          created_at
          agriculture_analysis_data {
            id
            orthophoto
            orthophoto_png
            phase
            index_data(order_by: {name: asc}) {
              name
              png
              tiff
              average
              min
              max
              std_dev
            }
          }
        }
        lat_lngs(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
          lat
          lng
        }
        annotations(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
          uuid
          created_at
          annotation
          position(where: `+whereNotDeleted()+`, order_by: {id: asc}) {
            lat
            lng
          }
        }
      }
    }
  `), {
    variables: {
      fieldId,
    }
  });

  const [ deleteAnnotation ] = useMutation(gql(`
    mutation DeleteAnnotation($uuid: uuid!) {
      update_annotations(where: {uuid: {_eq: $uuid}}, _set: {deleted_at: "now()"}) {
        affected_rows
      }
    }
  `));

  const getImageFromIndex = (index?: string): IMAGE_KEY => {
    switch (index?.toLowerCase()) {
      case 'orthophoto':
        return IMAGE_ORTHOPHOTO;
      case 'vari':
        return IMAGE_VARI_TIFF;
      case 'grvi':
        return IMAGE_GRVI_TIFF;
      case 'ndvi':
        return IMAGE_NDVI_TIFF;
      case 'gndvi':
        return IMAGE_GNDVI_TIFF;
      case 'osavi':
        return IMAGE_OSAVI_TIFF;
      case 'cvi':
        return IMAGE_CVI_TIFF;
      case 'ndwi':
        return IMAGE_NDWI_TIFF;
      case 'ndrei':
        return IMAGE_NDREI_TIFF;
      case 'lci':
        return IMAGE_LCI_TIFF;
      default:
        return IMAGE_ORTHOPHOTO;
    }
  }

  React.useEffect(() => {
    setSelectedRange(undefined);
  }, [selectedIndex]);

  React.useEffect(() => {
    setCompareSelectedRange(undefined);
  }, [compareIndex]);

  React.useEffect(() => {
    if (data) {
      setField(data.fields[0]);
      setAnnotations(data.fields[0].annotations.filter((a: any) => a.position.length > 0).map((a: any) => {
        return {
          uuid: a.uuid,
          date: new Date(a.created_at),
          position: { lat: a.position[0].lat, lng: a.position[0].lng },
          annotation: a.annotation,
          audioBlobs: [],
          pictureBlobs: [],
          attachments: [],
        } as Annotation;
      }));
      setAnalysis(data.fields[0].analyses);
      let currAnalysis: any | null;
      if (!currentAnalysis) {
        currAnalysis = data.fields[0].analyses.find((a: any) => a.uuid === analysisUUID) ?? data.fields[0].analyses[0];
        setCurrentAnalysis(currAnalysis);
        if (currAnalysis) {
          const url = new URL(window.location.href);
          url.searchParams.set('analysis', currAnalysis.uuid);
          window.history.replaceState({}, document.title, url.toString());
        }
      } else {
        currAnalysis = currentAnalysis;
        setCurrentAnalysis(currAnalysis);
      }
      const compareAnalysis = data.fields[0].analyses.find((a: any) => a.uuid === compareAnalysisID);
      setCompareAnalysis(compareAnalysis ?? null);

      const b = new google.maps.LatLngBounds();
      const tmpLatLngs: google.maps.LatLngLiteral[] = [];
      data.fields[0].lat_lngs.forEach((latLng: any) => {
        b.extend(new google.maps.LatLng(latLng.lat, latLng.lng));
        tmpLatLngs.push({ lat: latLng.lat, lng: latLng.lng });
      });
      setDefaultCenter(b.getCenter().toJSON());
      setDefaultZoom(computeZoom(b, window.innerWidth, window.innerHeight));
      setLatLngs(tmpLatLngs);

      db.fields.put({
        id: data.fields[0].uuid,
        bounds: b.toJSON(),
      }).then(() => {});

      const aIdx = [];
      if (data.fields[0].analyses.length > 0) {
        const tmp = currAnalysis.agriculture_analysis_data;
        if (tmp && tmp.length > 0 && (tmp[0].orthophoto || tmp[0].orthophoto_png)) {
          aIdx.push('Orthophoto');
        }
        if (tmp && tmp.length > 0 && tmp[0].index_data) {
          for (const idx of tmp[0].index_data) {
            aIdx.push(idx.name.toUpperCase());
          }
        }
      }
      setAvailableIndexes(aIdx);
    } else {
      setLatLngs([]);
      setField(null);
      setAnalysis([]);
      setCurrentAnalysis(null);
      setCompareAnalysis(null);
      setAvailableIndexes([]);
      setDefaultCenter(undefined);
      setDefaultZoom(undefined);
    }
  }, [data, currentAnalysis, analysisUUID, compareAnalysisID]);

  React.useEffect(() => {
    const phase = currentAnalysis?.agriculture_analysis_data[0]?.phase;
    setMarks(getMarksForPhase(phase));
  }, [currentAnalysis]);

  React.useEffect(() => {
    const phase = compareAnalysis?.agriculture_analysis_data[0]?.phase;
    setCompareMarks(getMarksForPhase(phase));
  }, [compareAnalysis]);

  const handleCameraChange = React.useCallback((ev: MapCameraChangedEvent) => {
    setCameraState(ev.detail);
  }, []);

  const onBoundsChange = (bounds: google.maps.LatLngBounds) => {
    if (!currentAnalysis) {
      return;
    }

    db.analyses.put({
      id: currentAnalysis.uuid,
      bounds: bounds.toJSON(),
    }).then(() => {});
  }

  const onAnnotateDialogClose = (annotation: Annotation | undefined) => {
    if (annotation && clickLatLng) {
      annotation.uuid = annotations.length + '';
      annotation.position = clickLatLng;
      annotation.date = new Date();
      setAnnotations([...annotations, annotation]);
    }
    setAnnotateDialogOpen(false);
  }

  if (!defaultCenter) {
    return (
      <React.Fragment>
        <Loading open />
      </React.Fragment>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: vertical ? 'block' : 'flex',
    }}>
      <BackButton />
      <Annotate open={annotateDialogOpen} onClose={onAnnotateDialogClose} latLng={clickLatLng} fieldID={field?.id ?? 0} />
      <div style={{
        width: vertical ? '100%' : '50%',
        height: vertical ? '50%' : '100%',
        borderBottom: vertical ? '2px solid white' : 'none',
        borderRight: vertical ? 'none' : '2px solid white',
      }}>
        <MapFull
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={ locked && cameraState ? cameraState.center : undefined }
        zoom={ locked && cameraState ? cameraState.zoom : undefined }
        onClick={(e) => {
          setClickLatLng(e.detail.latLng);
          setAnnotateDialogOpen(true);
        }}>
          { /* <Polygon path={latLngs} zIndex={-100} /> */ }
          { globalAvailableIndexes.map((gIndex) => {
            if (!currentAnalysis || selectedIndex !== gIndex.id || !gIndex.imageKey) {
              return null;
            }
            if (gIndex.id === 'Orthophoto') {
              return (
                <AnalysisLayer key={gIndex.id} analysisID={currentAnalysis.uuid} image={IMAGE_ORTHOPHOTO}
                  height={ vertical ? '50%' : '100%' } width={ vertical ? '100%' : '50%'}
                  onBoundsChange={onBoundsChange} rgb />
              );
            }
            return (
              <AnalysisLayer key={gIndex.id} analysisID={currentAnalysis.uuid} image={gIndex.imageKey}
                height={ vertical ? '50%' : '100%' } width={ vertical ? '100%' : '50%'}
                onBoundsChange={onBoundsChange} range={selectedRange}
                clipMin={-1} clipMax={1} rangeMin={0} absolute />
            );
          })}
          <Polyline path={latLngs} closed />
          { showButtons && <FloatingTopBar
            availableIndexes={availableIndexes}
            selectedIndex={getIndex(selectedIndex)}
            onIndexChange={(index) => {
              setSelectedIndex(index.id);
              if (index) {
                const url = new URL(window.location.href);
                url.searchParams.set('index', index.id);
                window.history.replaceState({}, document.title, url.toString());
              }
            }} /> }
          <UserPosition />
          { showButtons && <TimeLine
            analysis={analysis}
            currentIndex={selectedIndex}
            currentAnalysis={currentAnalysis ?? undefined} onAnalysisSelected={(analysisUuid) => {
              setCurrentAnalysis(analysis.find((a) => a.uuid === analysisUuid) ?? null);
              if (analysisUuid) {
                const url = new URL(window.location.href);
                url.searchParams.set('analysis', analysisUuid);
                window.history.replaceState({}, document.title, url.toString());
              }
            }} /> }
          { annotations.map((annotation) => (
            <AnnotationMarker key={annotation.uuid} annotation={annotation} onDelete={() => {
              deleteAnnotation({
                variables: {
                  uuid: annotation.uuid,
                }
              }).then(() => {
                setAnnotations(annotations.filter((a) => a.uuid !== annotation.uuid));
              }).catch((e) => {
                console.error("Failed to delete annotation", e);
              });
            }} />
          ))}
          { selectedIndex && selectedIndex !== 'Orthophoto' && selectedIndex !== 'GLB' && selectedIndex !== 'None' &&
            <IndexSlider
              splitColor
              min={min}
              max={max}
              onSelectedRange={(range) => {
                setSelectedRange(range);
              }}
              marks={marks}
              showLabels={marks && marks.length > 0}
            /> 
          }
        </MapFull>
      </div>
      <div style={{
        width: vertical ? '100%' : '50%',
        height: vertical ? '50%' : '100%',
        borderTop: vertical ? '2px solid white' : 'none',
        borderLeft: vertical ? 'none' : '2px solid white',
      }}>
        <MapFull
        defaultCenter={defaultCenter}
        defaultZoom={defaultZoom}
        center={ locked && cameraState ? cameraState.center : undefined }
        zoom={ locked && cameraState ? cameraState.zoom : undefined }
        onCameraChanged={handleCameraChange}
        onClick={(e) => {
          setClickLatLng(e.detail.latLng);
          setAnnotateDialogOpen(true);
        }}>
          { /* <Polygon path={latLngs} zIndex={-100} /> */ }
          { compareAnalysisID && <AnalysisLayer
            analysisID={compareAnalysisID}
            image={getImageFromIndex(compareIndex)}
            height={ vertical ? '50%' : '100%' }
            width={ vertical ? '100%' : '50%'}
            top={ vertical ? '50%' : '0' }
            left={ vertical ? '0' : '50%' }
            rgb={compareIndex === 'Orthophoto'}
            onBoundsChange={onBoundsChange}
            // setMin={setCompareMin}
            // setMax={setCompareMax}
            range={compareIndex === 'Orthophoto' ? undefined : compareSelectedRange}
            clipMin={compareIndex === 'Orthophoto' ? undefined : -1}
            clipMax={compareIndex === 'Orthophoto' ? undefined : 1}
            rangeMin={compareIndex === 'Orthophoto' ? undefined : 0}
            absolute={compareIndex !== 'Orthophoto'} /> }
          <Polyline path={latLngs} closed />
          <UserPosition />
          { annotations.map((annotation) => (
            <AnnotationMarker key={annotation.uuid} annotation={annotation} onDelete={() => {
              deleteAnnotation({
                variables: {
                  uuid: annotation.uuid,
                }
              }).then(() => {
                setAnnotations(annotations.filter((a) => a.uuid !== annotation.uuid));
              }).catch((e) => {
                console.error("Failed to delete annotation", e);
              });
            }} />
          ))}
          { compareIndex && compareIndex !== 'Orthophoto' && selectedIndex !== 'GLB' &&
            <IndexSlider
              splitColor
              min={compareMin}
              max={compareMax}
              onSelectedRange={(range) => {
                setCompareSelectedRange(range);
              }}
              marks={compareMarks}
              showLabels={compareMarks && compareMarks.length > 0}
            />
          }
        </MapFull>
      </div>
      <Fab style={{
        position: 'absolute',
        bottom: 80,
        right: 10,
      }} onClick={() => setShowButtons(!showButtons)}>
        { showButtons ? <VisibilityIcon /> : <VisibilityOffIcon /> }
      </Fab>
      <Fab style={{
        position: 'absolute',
        bottom: 10,
        right: 10,
      }} color={locked ? 'primary' : 'default'} onClick={() => setLocked(!locked)}>
        { locked ? <LockIcon /> : <LockOpenIcon /> }
      </Fab>
    </div>
  );
}

export default Compare;
